import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";
import { apiUrl } from "../../utils/apiUrl";
import "./mosaicWall.css";

// Explicit stack so the canvas mask can never fall back to a serif default.
const MOSAIC_FONT = "Arial, Helvetica, sans-serif";

function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

function isWordMode(themeData) {
  return String(themeData?.submission_mode || "").trim().toLowerCase() === "word";
}

/** @returns {{ word: string, userId: string, key: string }[]} */
function parsePlayerWordEntries(raw) {
  const out = [];
  const push = (word, userId, key) => {
    const w = String(word || "").trim();
    if (!w) return;
    out.push({
      word: w.toUpperCase(),
      userId: userId != null ? String(userId) : "",
      key: key != null ? String(key) : w,
    });
  };

  if (Array.isArray(raw)) {
    raw.forEach((item, i) => {
      if (typeof item === "string") push(item, "", `${item}-${i}`);
      else if (item && typeof item === "object" && item.word != null) {
        push(item.word, item.userId, item.userId || item.at || `${item.word}-${i}`);
      }
    });
    return out;
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      return parsePlayerWordEntries(JSON.parse(raw));
    } catch {
      push(raw, "", raw);
    }
  }
  return out;
}

/** Stable distinct color per user/word — CORE reference palette. */
const CORE_WORD_COLORS = [
  "#1a3a6b", // navy
  "#e67e22", // orange
  "#1abc9c", // teal
  "#c0392b", // red
  "#d4a017", // gold
  "#27ae60", // green
  "#2980b9", // blue
  "#8e44ad", // purple
];

function colorForEntry(entry, index) {
  let hash = index * 47;
  const seed = entry.userId || entry.key || entry.word || "";
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return CORE_WORD_COLORS[hash % CORE_WORD_COLORS.length];
}

export default function MosaicWall() {
  const { data: themeData } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const outlineRef = useRef(null);
  const stageRef = useRef(null);
  const activePreviewTileRef = useRef(null);

  const wordMode = isWordMode(themeData);
  const word = String(themeData?.words || "").trim().toUpperCase();
  const outlineColor = themeData?.text_color || "#1a2b6d";
  const playerEntries = useMemo(
    () => parsePlayerWordEntries(themeData?.player_words),
    [themeData?.player_words]
  );

  const parseToPx = (val, fallback) => {
    if (val == null) return fallback;
    if (typeof val === "number" && Number.isFinite(val) && val > 0) return Math.floor(val);
    const raw = String(val).trim().toLowerCase();
    if (!raw) return fallback;

    const m = raw.match(/^(\d+(?:\.\d+)?)(px|rem|em)?$/);
    if (!m) return fallback;
    const num = parseFloat(m[1]);
    if (!Number.isFinite(num) || num <= 0) return fallback;

    const unit = m[2] || "px";
    if (unit === "px") return Math.floor(num);

    const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize || "16") || 16;
    if (unit === "rem") return Math.floor(num * rootPx);
    if (unit === "em") {
      const el = containerRef.current || document.body;
      const elPx = parseFloat(getComputedStyle(el).fontSize || String(rootPx)) || rootPx;
      return Math.floor(num * elPx);
    }

    return fallback;
  };

  const measureWordLayout = useCallback(
    (ctx, width, height) => {
      const cleanWord = word.trim();
      const firstSpaceIndex = cleanWord.indexOf(" ");
      const lines =
        firstSpaceIndex !== -1
          ? [cleanWord.substring(0, firstSpaceIndex), cleanWord.substring(firstSpaceIndex + 1)]
          : [cleanWord];

      const maxTextWidth = width * 0.92;
      const maxTextHeight = height * 0.9;
      const REF = 100;
      ctx.font = `bold ${REF}px ${MOSAIC_FONT}`;
      const widestRef = Math.max(...lines.map((l) => ctx.measureText(l).width)) || 1;
      const widthLimited = (maxTextWidth / widestRef) * REF;
      const heightLimited = maxTextHeight / (lines.length * 1.2);
      const autoFitFontSize = Math.max(12, Math.floor(Math.min(widthLimited, heightLimited)));

      const adminFontPxRaw = parseToPx(themeData?.word_font_size, null);
      const adminFontPx =
        adminFontPxRaw != null
          ? window.innerWidth < 768
            ? Math.round(adminFontPxRaw * 0.75)
            : adminFontPxRaw
          : null;
      const fontSize =
        adminFontPx != null ? Math.max(12, Math.min(autoFitFontSize, adminFontPx)) : autoFitFontSize;

      return { cleanWord, lines, fontSize };
    },
    [word, themeData?.word_font_size]
  );

  const drawWordOnCanvas = useCallback(
    (ctx, width, height, { fill = true, stroke = false, strokeColor = outlineColor, lineWidth = 2 } = {}) => {
      const { cleanWord, lines, fontSize } = measureWordLayout(ctx, width, height);
      ctx.font = `bold ${fontSize}px ${MOSAIC_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.miterLimit = 2;

      const paintFillAt = (targetCtx, ox = 0, oy = 0) => {
        if (lines.length > 1) {
          const lineHeight = fontSize * 1.2;
          const totalHeight = lines.length * lineHeight;
          lines.forEach((line, index) => {
            const x = width / 2 + ox;
            const y = height / 2 - totalHeight / 2 + index * lineHeight + lineHeight / 2 + oy;
            targetCtx.fillText(line, x, y);
          });
        } else {
          targetCtx.fillText(cleanWord, width / 2 + ox, height / 2 + oy);
        }
      };

      if (fill) {
        ctx.fillStyle = "black";
        paintFillAt(ctx);
      }

      if (stroke) {
        // strokeText traces EVERY glyph edge (outer + holes in D/A, internals in N)
        // → looks like 2–3 borders. Build ONE outer silhouette ring instead.
        const ring = document.createElement("canvas");
        ring.width = width;
        ring.height = height;
        const rctx = ring.getContext("2d");
        rctx.font = `bold ${fontSize}px ${MOSAIC_FONT}`;
        rctx.textAlign = "center";
        rctx.textBaseline = "middle";
        rctx.fillStyle = strokeColor;

        const radius = Math.max(1, lineWidth);
        const steps = Math.max(16, radius * 10);
        for (let i = 0; i < steps; i++) {
          const a = (i / steps) * Math.PI * 2;
          paintFillAt(rctx, Math.cos(a) * radius, Math.sin(a) * radius);
        }
        for (let d = 1; d <= radius; d++) {
          paintFillAt(rctx, d, 0);
          paintFillAt(rctx, -d, 0);
          paintFillAt(rctx, 0, d);
          paintFillAt(rctx, 0, -d);
        }

        // Punch out the letter interior → leave only the outer border.
        rctx.globalCompositeOperation = "destination-out";
        paintFillAt(rctx, 0, 0);

        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(ring, 0, 0);
      }

      // Smooth outline — do not pixelate this canvas.
      return fontSize;
    },
    [measureWordLayout, outlineColor]
  );

  const loadMosaic = useCallback(async () => {
    if (!adminToken || wordMode) return;
    try {
      const resp = await fetch(apiUrl("/yogaGallery"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ userId: user?.userId || "", sort: "newest" }),
      });
      const payload = await resp.json();
      const uploads = resp.ok && payload?.success && Array.isArray(payload.uploads) ? payload.uploads : [];
      setImages(
        uploads
          .filter((u) => u.mediaType === "image" && u.uploadFile)
          .map((u) => s3ThemeAssetUrl(u.uploadFile))
      );
    } catch (e) {
      console.error("mosaicWall", e);
      setImages([]);
    }
  }, [adminToken, user?.userId, wordMode]);

  useEffect(() => {
    loadMosaic();
  }, [loadMosaic]);

  const buildMosaic = useCallback(() => {
    const el = containerRef.current;
    const outlineEl = outlineRef.current;
    if (!el || !word) return;

    while (el.firstChild) el.removeChild(el.firstChild);

    const width = el.offsetWidth;
    const height = el.offsetHeight;
    if (!width || !height) return;

    // —— Word mode: CORE-style — packed word tiles inside letters + one outer outline ——
    if (wordMode) {
      el.style.backgroundColor = "transparent";
      el.style.display = "block";
      el.style.position = "relative";
      el.style.alignItems = "";
      el.style.justifyContent = "";
      el.style.gridTemplateColumns = "";
      el.style.gridAutoRows = "";

      // Filled letter mask — always on so player words stay inside CORE when
      // the screen/zoom scales (px tiles + %-sized stage stay clipped together).
      const maskCanvas = document.createElement("canvas");
      maskCanvas.width = width;
      maskCanvas.height = height;
      const maskCtx = maskCanvas.getContext("2d");
      drawWordOnCanvas(maskCtx, width, height, { fill: true, stroke: false });
      const maskURL = maskCanvas.toDataURL("image/png");
      el.style.webkitMaskImage = `url(${maskURL})`;
      el.style.maskImage = `url(${maskURL})`;
      el.style.webkitMaskSize = "100% 100%";
      el.style.maskSize = "100% 100%";
      el.style.webkitMaskPosition = "0 0";
      el.style.maskPosition = "0 0";
      el.style.overflow = "hidden";
      el.style.zIndex = "";
      el.style.setProperty("--stage-w", `${width}px`);
      el.style.setProperty("--stage-h", `${height}px`);

      // Single outer border on top (not masked).
      if (outlineEl) {
        outlineEl.width = width;
        outlineEl.height = height;
        const octx = outlineEl.getContext("2d");
        octx.clearRect(0, 0, width, height);
        drawWordOnCanvas(octx, width, height, {
          fill: false,
          stroke: true,
          strokeColor: outlineColor,
          lineWidth: 3,
        });
      }

      if (!playerEntries.length) return;

      const probe = document.createElement("canvas").getContext("2d");
      const { cleanWord, lines, fontSize: mosaicFontSize } = measureWordLayout(
        probe,
        width,
        height
      );
      probe.font = `bold ${mosaicFontSize}px ${MOSAIC_FONT}`;

      /** One region per mosaic glyph — words must stay inside a single letter. */
      const letterRegions = [];
      const addLineLetters = (line, centerY) => {
        const totalW = probe.measureText(line).width || 1;
        let x = width / 2 - totalW / 2;
        for (const ch of line) {
          if (ch === " ") {
            x += probe.measureText(" ").width;
            continue;
          }
          const gw = Math.max(1, probe.measureText(ch).width);
          const centerX = x + gw / 2;

          const letterCanvas = document.createElement("canvas");
          letterCanvas.width = width;
          letterCanvas.height = height;
          const lctx = letterCanvas.getContext("2d");
          lctx.font = `bold ${mosaicFontSize}px ${MOSAIC_FONT}`;
          lctx.textAlign = "center";
          lctx.textBaseline = "middle";
          lctx.fillStyle = "#000";
          lctx.fillText(ch, centerX, centerY);
          const data = lctx.getImageData(0, 0, width, height).data;

          // Letter pixel bounds (for denser horizontal packing).
          let minX = width;
          let maxX = 0;
          let minY = height;
          let maxY = 0;
          for (let py = 0; py < height; py++) {
            for (let px = 0; px < width; px++) {
              if (data[(py * width + px) * 4 + 3] > 40) {
                if (px < minX) minX = px;
                if (px > maxX) maxX = px;
                if (py < minY) minY = py;
                if (py > maxY) maxY = py;
              }
            }
          }

          letterRegions.push({
            char: ch,
            centerX,
            centerY,
            data,
            minX,
            maxX,
            minY,
            maxY,
            placed: [],
          });
          x += gw;
        }
      };

      if (lines.length > 1) {
        const lineHeight = mosaicFontSize * 1.2;
        const totalHeight = lines.length * lineHeight;
        lines.forEach((line, index) => {
          const y =
            height / 2 - totalHeight / 2 + index * lineHeight + lineHeight / 2;
          addLineLetters(line, y);
        });
      } else {
        addLineLetters(cleanWord, height / 2);
      }
      if (!letterRegions.length) return;

      const alphaAt = (data, px, py) => {
        const x = Math.min(width - 1, Math.max(0, Math.floor(px)));
        const y = Math.min(height - 1, Math.max(0, Math.floor(py)));
        return data[(y * width + x) * 4 + 3];
      };

      const EDGE = 2;
      const pointInside = (data, cx, cy) => alphaAt(data, cx, cy) > 35;

      const measure = document.createElement("canvas").getContext("2d");
      const measureText = (text, fontSize) => {
        measure.font = `800 ${fontSize}px ${MOSAIC_FONT}`;
        return { w: measure.measureText(text).width, h: fontSize * 0.92 };
      };

      const makeBox = (cx, cy, tw, th) => ({
        left: cx - tw / 2,
        top: cy - th / 2,
        right: cx + tw / 2,
        bottom: cy + th / 2,
      });

      const overlaps = (a, b, gap = 2) =>
        !(
          a.right + gap <= b.left ||
          b.right + gap <= a.left ||
          a.bottom + gap <= b.top ||
          b.bottom + gap <= a.top
        );

      const boxInsideLetter = (data, box, { strict = true } = {}) => {
        const midX = (box.left + box.right) / 2;
        const midY = (box.top + box.bottom) / 2;
        if (!strict) {
          // Center + mid-edges — enough to keep word in the letter without hiding it.
          const pts = [
            [midX, midY],
            [midX, box.top],
            [midX, box.bottom],
            [box.left, midY],
            [box.right, midY],
          ];
          return pts.every(([x, y]) => pointInside(data, x, y));
        }
        const pts = [
          [box.left, box.top],
          [box.right, box.top],
          [box.left, box.bottom],
          [box.right, box.bottom],
          [midX, box.top],
          [midX, box.bottom],
          [box.left, midY],
          [box.right, midY],
          [midX, midY],
        ];
        return pts.every(([x, y]) => pointInside(data, x, y));
      };

      // Horizontal spans inside each letter (for dense L→R packing).
      const buildRows = (region) => {
        const rows = [];
        for (let y = region.minY + EDGE; y <= region.maxY - EDGE; y += 2) {
          let runStart = -1;
          for (let x = region.minX; x <= region.maxX + 1; x++) {
            const inside =
              x <= region.maxX && pointInside(region.data, x, y);
            if (inside && runStart < 0) runStart = x;
            if (!inside && runStart >= 0) {
              const minX = runStart;
              const maxX = x - 1;
              if (maxX - minX >= 10) {
                rows.push({ y, minX, maxX, w: maxX - minX });
              }
              runStart = -1;
            }
          }
        }
        return rows;
      };

      for (const region of letterRegions) {
        region.rows = buildRows(region);
        region.placed = [];
      }
      const usableLetters = letterRegions.filter((r) => r.rows.length > 0);
      if (!usableLetters.length) return;

      const avgLetterH =
        usableLetters.reduce((s, r) => s + (r.maxY - r.minY), 0) /
        usableLetters.length;

      // Fit every unique word; fillers fill remaining space.
      const maxFont = Math.max(18, Math.min(48, avgLetterH * 0.28));
      const minFont = 5;
      const GAP = 1;
      const allPlaced = [];
      const placedUniqueKeys = new Set();

      const pickLetter = () => {
        let best = usableLetters[0];
        let bestScore = Infinity;
        for (const r of usableLetters) {
          const score = r.placed.length;
          if (score < bestScore) {
            bestScore = score;
            best = r;
          }
        }
        return best;
      };

      const tryPlaceInRegion = (
        region,
        entry,
        index,
        fontSize,
        { strict = true, allowOverlap = false, markUnique = false } = {}
      ) => {
        const text = entry.word;
        const { w: tw, h: th } = measureText(text, fontSize);
        if (tw < 4 || th < 4) return false;

        const rowOrder = [...region.rows];
        if (!markUnique) shuffleArray(rowOrder);

        for (const row of rowOrder) {
          const pad = EDGE;
          if (row.w < tw + pad * 2) continue;
          const start = row.minX + tw / 2 + pad;
          const end = row.maxX - tw / 2 - pad;
          if (end < start) continue;

          const step = Math.max(1, Math.min(4, Math.floor(tw * 0.08)));
          const xs = [];
          for (let x = start; x <= end; x += step) xs.push(x);
          if (!markUnique && Math.random() < 0.35) shuffleArray(xs);

          for (const cx of xs) {
            const cy = row.y;
            const box = makeBox(cx, cy, tw + 1, th + 1);
            if (!boxInsideLetter(region.data, box, { strict })) continue;
            if (
              !allowOverlap &&
              region.placed.some((p) => overlaps(box, p.box, GAP))
            ) {
              continue;
            }

            const rec = {
              entry,
              index,
              cx,
              cy,
              angle: 0,
              fontSize,
              tw,
              th,
              box,
              isUnique: markUnique,
            };
            region.placed.push(rec);
            allPlaced.push(rec);
            if (markUnique) placedUniqueKeys.add(entry.key);
            return true;
          }
        }
        return false;
      };

      const tryPlaceAnywhere = (
        entry,
        index,
        fontSize,
        preferLetter,
        opts = {}
      ) => {
        const order = preferLetter
          ? [
              preferLetter,
              ...usableLetters.filter((r) => r !== preferLetter),
            ]
          : [...usableLetters].sort((a, b) => a.placed.length - b.placed.length);
        for (const region of order) {
          if (tryPlaceInRegion(region, entry, index, fontSize, opts)) return true;
        }
        return false;
      };

      // Vocabulary: full phrases + space-split tokens (short words pack denser).
      const vocab = [];
      playerEntries.forEach((entry, index) => {
        vocab.push({ entry, index, weight: 2, len: entry.word.length });
        const parts = String(entry.word)
          .split(/\s+/)
          .filter((p) => p.length >= 3);
        if (parts.length > 1) {
          parts.forEach((part, pi) => {
            vocab.push({
              entry: {
                word: part,
                userId: entry.userId,
                key: `${entry.key}-t${pi}`,
              },
              index,
              weight: 1,
              len: part.length,
            });
          });
        }
      });

      // —— Pass 1: each submission once, only if the whole word fits inside a letter ——
      const uniques = playerEntries.map((entry, index) => ({
        entry,
        index,
        len: entry.word.length,
      }));
      uniques.sort((a, b) => a.len - b.len);

      for (const item of uniques) {
        let placed = false;
        for (let fs = Math.round(maxFont); fs >= minFont && !placed; fs -= 1) {
          placed = tryPlaceAnywhere(item.entry, item.index, fs, pickLetter(), {
            strict: true,
            markUnique: true,
          });
        }
      }

      // —— Pass 2: dense gap-fill (repeats OK) without covering uniques ——
      const sizeLadder = [];
      for (let fs = Math.round(maxFont * 0.85); fs >= minFont; fs -= 1) {
        sizeLadder.push(fs);
      }

      const targetCount = Math.min(
        400,
        Math.max(
          playerEntries.length * 3,
          Math.round(
            usableLetters.reduce(
              (s, r) => s + ((r.maxX - r.minX) * (r.maxY - r.minY)) / 700,
              0
            )
          )
        )
      );

      let missStreak = 0;
      let guard = 0;
      const shuffledVocab = () => {
        const v = [...vocab];
        shuffleArray(v);
        return v;
      };

      for (const fs of sizeLadder) {
        if (allPlaced.length >= targetCount) break;
        missStreak = 0;
        const batch = shuffledVocab();
        for (const item of batch) {
          if (allPlaced.length >= targetCount) break;
          if (guard++ > targetCount * 30) break;
          if (item.len > 14 && fs < maxFont * 0.45) continue;
          const ok = tryPlaceAnywhere(item.entry, item.index, fs, pickLetter(), {
            strict: true,
            markUnique: false,
          });
          if (ok) missStreak = 0;
          else missStreak += 1;
          if (missStreak > batch.length * 2) break;
        }
      }

      // —— Pass 3: tiny fill ——
      if (allPlaced.length < targetCount) {
        for (let fs = Math.min(12, Math.round(maxFont * 0.4)); fs >= minFont; fs -= 1) {
          if (allPlaced.length >= targetCount) break;
          let hits = 0;
          for (const item of shuffledVocab().filter((v) => v.len <= 12)) {
            if (allPlaced.length >= targetCount) break;
            if (
              tryPlaceAnywhere(item.entry, item.index, fs, pickLetter(), {
                strict: true,
                markUnique: false,
              })
            ) {
              hits += 1;
            }
          }
          if (hits === 0) break;
        }
      }

      if (!allPlaced.length) return;

      const fragment = document.createDocumentFragment();
      const stageEl = el.parentElement;
      // Older builds painted uniques above the mask; drop any leftovers.
      stageEl?.querySelectorAll(".mosaic-word-tile--unique").forEach((n) => n.remove());

      const clearZoom = () => {
        stageEl?.querySelectorAll(".mosaic-word-tile-zoom").forEach((n) => n.remove());
        activePreviewTileRef.current = null;
      };

      const showWordPreview = (tile, text, color) => {
        clearZoom();
        if (!stageEl) return;
        const preview = document.createElement("div");
        preview.className = "mosaic-word-tile-zoom";
        preview.textContent = text;
        preview.style.color = color;
        preview.style.left = `${el.offsetLeft + tile.offsetLeft + tile.offsetWidth / 2}px`;
        preview.style.top = `${el.offsetTop + tile.offsetTop + tile.offsetHeight / 2}px`;
        stageEl.appendChild(preview);
        activePreviewTileRef.current = tile;
      };

      allPlaced.forEach((p, i) => {
        const color = colorForEntry(p.entry, p.index);
        const tileW = p.tw + 2;
        const tileH = p.th + 1;

        const tile = document.createElement("div");
        tile.className = "mosaic-word-tile";
        tile.style.left = `${((p.cx - tileW / 2) / width) * 100}%`;
        tile.style.top = `${((p.cy - tileH / 2) / height) * 100}%`;
        tile.style.width = `${(tileW / width) * 100}%`;
        tile.style.height = `${(tileH / height) * 100}%`;
        tile.style.color = color;
        tile.style.fontSize = `calc(var(--stage-h) * ${p.fontSize / height})`;
        tile.style.setProperty("--tilt", `0deg`);
        tile.style.setProperty("--i", String(Math.min(i, 40)));
        tile.textContent = p.entry.word;
        tile.title = p.entry.word;
        tile.setAttribute("role", "button");
        tile.tabIndex = 0;

        tile.addEventListener("mouseenter", () => {
          showWordPreview(tile, p.entry.word, color);
        });
        tile.addEventListener("mouseleave", clearZoom);

        tile.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (activePreviewTileRef.current === tile) {
            clearZoom();
            return;
          }
          showWordPreview(tile, p.entry.word, color);
        });

        fragment.appendChild(tile);
      });

      if (stageEl && !stageEl.dataset.previewDismissBound) {
        stageEl.dataset.previewDismissBound = "1";
        stageEl.addEventListener("click", (e) => {
          if (e.target?.closest?.(".mosaic-word-tile")) return;
          clearZoom();
        });
      }

      el.appendChild(fragment);
      if (stageEl) {
        stageEl.style.setProperty("--stage-w", `${width}px`);
        stageEl.style.setProperty("--stage-h", `${height}px`);
      }
      return;
    }

    // —— Image mode ——
    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = width;
    maskCanvas.height = height;
    const maskCtx = maskCanvas.getContext("2d");
    drawWordOnCanvas(maskCtx, width, height, { fill: true, stroke: false });
    const maskURL = maskCanvas.toDataURL("image/png");
    el.style.webkitMaskImage = `url(${maskURL})`;
    el.style.maskImage = `url(${maskURL})`;

    el.style.display = "grid";
    el.style.position = "";
    el.style.alignItems = "";
    el.style.justifyContent = "";

    if (outlineEl) {
      const octx = outlineEl.getContext("2d");
      octx.clearRect(0, 0, outlineEl.width || 0, outlineEl.height || 0);
    }

    if (images.length === 0) {
      el.style.backgroundColor = outlineColor;
      return;
    }
    el.style.backgroundColor = "";

    const cellSize = window.innerWidth < 768 ? 30 : 40;
    el.style.gridTemplateColumns = `repeat(auto-fill, ${cellSize}px)`;
    el.style.gridAutoRows = `${cellSize}px`;
    const cols = Math.floor(width / cellSize);
    const rows = Math.floor(height / cellSize);
    const totalTiles = cols * rows;
    if (totalTiles <= 0) return;

    const positions = [];
    for (let i = 0; i < totalTiles; i++) positions.push(i);
    shuffleArray(positions);

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < totalTiles; i++) {
      const img = document.createElement("img");
      img.src = images[i % images.length];
      img.alt = "";
      img.loading = "lazy";
      img.style.gridArea = `${Math.floor(positions[i] / cols) + 1} / ${(positions[i] % cols) + 1} / auto / auto`;
      img.style.animationDelay = `${i * 2}ms`;
      fragment.appendChild(img);
    }
    el.appendChild(fragment);
  }, [word, outlineColor, images, wordMode, playerEntries, drawWordOnCanvas, measureWordLayout]);

  useEffect(() => {
    if (!word) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    let cancelled = false;
    let id;
    const run = () => {
      id = setTimeout(() => {
        if (cancelled) return;
        buildMosaic();
        setLoading(false);
      }, 100);
    };
    if (document.fonts && document.fonts.load) {
      document.fonts
        .load(`bold 100px ${MOSAIC_FONT}`)
        .then(() => document.fonts.ready)
        .then(run)
        .catch(run);
    } else {
      run();
    }
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [buildMosaic, word]);

  useEffect(() => {
    if (!word) return undefined;
    const stage = stageRef.current;
    let resizeTimer;
    const rebuild = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => buildMosaic(), 150);
    };
    window.addEventListener("resize", rebuild);
    let ro;
    if (stage && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(rebuild);
      ro.observe(stage);
    }
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", rebuild);
      ro?.disconnect();
    };
  }, [buildMosaic, word]);

  if (!word) return null;

  return (
    <div className={`mosaic-word${wordMode ? " mosaic-word--text-mode" : ""}`}>
      <div className="mosaic-stage" ref={stageRef}>
        <div className="masked-letter" ref={containerRef} data-letter={word} />
        {wordMode ? (
          <canvas
            className="mosaic-word-outline"
            ref={outlineRef}
            aria-hidden="true"
          />
        ) : null}
      </div>

      {loading ? (
        <div className="mosaic-loading">
          <div className="mosaic-spinner" />
          <p>Creating your mosaic...</p>
        </div>
      ) : null}
    </div>
  );
}
